(function() {
  'use strict';
  // Customer-facing copy comes from window.theme.strings (emitted by
  // snippets/head.liquid from the locale files) so it follows the storefront
  // language. The English argument stays as the fallback for any context where
  // theme.strings has not loaded.
  function themeStr(key, fallback) {
    var s = window.theme && window.theme.strings;
    return (s && s[key]) || fallback;
  }


  const TALLY_FORM_URL = 'https://tally.so/r/jae7l6';
  
  // Webhook Configuration - Initiate Checkout (when checkout button clicked from cart)
  const WEBHOOK_URL = 'https://n8n-latest-1-js6i.onrender.com/webhook/stripe-checkout/initiate';
  const WEBHOOK_TOKEN = 'IB98yh97b08G97Vvyf9797V7Vex5s34';
  
  // Function to fetch cart data
  async function getCartData() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) {
        throw new Error('Failed to fetch cart data');
      }
      return await response.json();
    } catch (error) {
      return null;
    }
  }

  // Function to format product GID
  function formatProductGID(productId) {
    return `gid://shopify/Product/${productId}`;
  }

  // Function to convert price to market currency - use helper if available
  function convertToMarketCurrency(priceInCents) {
    if (window.WebhookHelper && typeof window.WebhookHelper.convertToMarketCurrency === 'function') {
      return window.WebhookHelper.convertToMarketCurrency(priceInCents);
    }
    // Fallback to MarketConfig
    if (window.MarketConfig && typeof window.MarketConfig.convertPrice === 'function') {
      return window.MarketConfig.convertPrice(priceInCents);
    }
    // Fallback to old systems for backward compatibility
    if (window.MetaPixelEnAe && typeof window.MetaPixelEnAe.convertToAED === 'function') {
      return window.MetaPixelEnAe.convertToAED(priceInCents, true);
    }
    // Last resort fallback
    const rate = (window.AED_CONFIG && window.AED_CONFIG.rate) || 0.04;
    const inrAmount = priceInCents / 100;
    const convertedAmount = inrAmount * rate;
    return parseFloat(convertedAmount.toFixed(2));
  }
  
  // Function to round up price - use helper if available
  function roundUpPrice(price) {
    if (window.WebhookHelper && typeof window.WebhookHelper.roundUpPrice === 'function') {
      return window.WebhookHelper.roundUpPrice(price);
    }
    return Math.ceil(price);
  }

  // Keep convertToAED for backward compatibility (now uses MarketConfig)
  function convertToAED(priceInCents) {
    return convertToMarketCurrency(priceInCents);
  }

  // Function to get Meta Pixel identifiers - use helper if available
  function getMetaPixelIdentifiers() {
    if (window.WebhookHelper) {
      return {
        fbp: window.WebhookHelper.getFbpCookie(),
        fbc: window.WebhookHelper.getFbcCookie()
      };
    }
    
    // Fallback: manual cookie extraction
    const identifiers = { fbp: null, fbc: null };
    try {
      const fbpMatch = document.cookie.match(/(?:^|; )_fbp=([^;]+)/);
      if (fbpMatch && fbpMatch[1]) {
        identifiers.fbp = decodeURIComponent(fbpMatch[1]);
      }

      const fbcMatch = document.cookie.match(/(?:^|; )_fbc=([^;]+)/);
      if (fbcMatch && fbcMatch[1]) {
        const fbc = decodeURIComponent(fbcMatch[1]);
        if (!fbc.endsWith('.fbclid') && fbc.split('.').length >= 4) {
          identifiers.fbc = fbc;
        }
      }
    } catch (e) {
      // Silently ignore cookie reading errors
    }
    return identifiers;
  }

  // Generate event ID - use helper if available
  function generateEventId() {
    if (window.WebhookHelper && typeof window.WebhookHelper.generateEventId === 'function') {
      return window.WebhookHelper.generateEventId();
    }
    // Fallback: manual generation
    function generateHex(length) {
      return Array.from({length: length}, () => 
        Math.floor(Math.random() * 16).toString(16).toUpperCase()
      ).join('');
    }
    return 'sh-' + generateHex(8) + '-' + generateHex(4) + '-' + 
           generateHex(4) + '-' + generateHex(4) + '-' + generateHex(12);
  }

  // Collect contact details from user
  function collectContactDetails() {
    return new Promise((resolve, reject) => {
      const contactSheet = document.getElementById('contact-details-sheet');
      const dialog = document.getElementById('contact-details-dialog');
      
      if (!contactSheet || !dialog) {
        console.warn('[Tally Checkout] Contact details sheet not found');
        // If sheet doesn't exist, proceed without contact details
        resolve({ email: '', phone: '' });
        return;
      }

      let formSubmitted = false;

      // Set up event listener for form submission
      const handleContactCollected = (event) => {
        formSubmitted = true;
        const formData = event.detail;
        cleanup();
        resolve({
          email: formData.email || '',
          phone: formData.phone || ''
        });
      };

      // Set up event listener for dialog close (user cancelled)
      const handleDialogClose = () => {
        // If dialog closes without form submission, user cancelled
        if (!formSubmitted) {
          cleanup();
          reject(new Error('User cancelled contact details collection'));
        }
      };

      // Cleanup function
      const cleanup = () => {
        document.removeEventListener('contactDetailsCollected', handleContactCollected);
        dialog.removeEventListener('close', handleDialogClose);
      };

      // Listen for form submission (must be before opening dialog)
      document.addEventListener('contactDetailsCollected', handleContactCollected, { once: true });

      // Listen for dialog close
      dialog.addEventListener('close', handleDialogClose, { once: true });

      // Open the contact details sheet
      try {
        if (contactSheet.popupOpen && typeof contactSheet.popupOpen === 'function') {
          contactSheet.popupOpen();
        } else if (dialog.showModal && typeof dialog.showModal === 'function') {
          dialog.showModal();
          dialog.removeAttribute('inert');
          dialog.setAttribute('aria-hidden', 'false');
        } else {
          dialog.setAttribute('open', '');
          dialog.removeAttribute('inert');
          dialog.setAttribute('aria-hidden', 'false');
        }
      } catch (error) {
        console.error('[Tally Checkout] Error opening contact details sheet:', error);
        cleanup();
        // If we can't open the sheet, proceed without contact details
        resolve({ email: '', phone: '' });
      }
    });
  }

  // Send InitiateCheckout webhook (fire and forget - never blocks)
  function sendInitiateCheckoutWebhook(cart, localeInfo, contactDetails = {}, checkoutUrl = null) {
    try {
      const hiddenFields = {};
      const payload = buildWebhookPayload(cart, hiddenFields, localeInfo, contactDetails, checkoutUrl);
      
      // Use helper if available (for international markets), otherwise use manual fetch
      if (window.WebhookHelper && window.WebhookHelper.isInternationalMarket && window.WebhookHelper.isInternationalMarket()) {
        // Use helper's sendWebhook (fire and forget - doesn't return promise)
        window.WebhookHelper.sendWebhook('InitiateCheckout', payload);
      } else {
        // Fallback: manual fetch for India or when helper not available
        const eventId = generateEventId();
        const eventTime = Math.floor(Date.now() / 1000);
    const metaPixelIds = getMetaPixelIdentifiers();
    
        const fullPayload = {
          ...payload,
      event_name: 'InitiateCheckout',
      event_time: eventTime,
      event_id: eventId,
      event_source_url: window.location.href,
      action_source: 'website',
      fbp: metaPixelIds.fbp,
      fbc: metaPixelIds.fbc,
      user_data: {
        client_user_agent: navigator.userAgent,
        client_ip_address: null
          }
        };
        
        fetch(WEBHOOK_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Webhook-Token': WEBHOOK_TOKEN
          },
          body: JSON.stringify(fullPayload),
          keepalive: true
        }).catch(() => {
          // Silent failure - don't block user experience
        });
      }
    } catch (error) {
      // Silent failure - webhook should never block checkout
    }
  }

  // Function to build webhook payload for InitiateCheckout event
  function buildWebhookPayload(cart, hiddenFields, localeInfo, contactDetails = {}, checkoutUrl = null) {
    // Get current market info for catalog prefixing and gateway
    let marketInfo = null;
    let catalogPrefix = '';
    let gateway = 'razorpay';
    
    if (window.MarketConfig && typeof window.MarketConfig.getCurrentMarket === 'function') {
      marketInfo = window.MarketConfig.getCurrentMarket();
      if (marketInfo && marketInfo.config) {
        catalogPrefix = marketInfo.config.catalogPrefix || '';
        gateway = marketInfo.config.gateway || 'razorpay';
      }
    }
    
    // Build content_ids - use helper if available, otherwise MarketConfig
    const variantIds = cart.items.map(item => item.variant_id.toString());
    let contentIds = variantIds;
    if (window.WebhookHelper && typeof window.WebhookHelper.prefixContentIds === 'function') {
      contentIds = window.WebhookHelper.prefixContentIds(variantIds);
    } else if (window.MarketConfig && typeof window.MarketConfig.prefixContentIds === 'function') {
      contentIds = window.MarketConfig.prefixContentIds(variantIds);
    }
    
    // Build contents array with proper currency conversion for all markets
    const contents = cart.items.map((item, index) => {
      const variantId = item.variant_id.toString();
      const prefixedVariantId = contentIds[index] || variantId;
      
      // Use convertToMarketCurrency for all markets and round up to nearest whole number
      const unitPrice = roundUpPrice(convertToMarketCurrency(item.final_price));
      
      return {
        id: prefixedVariantId,
        sku: prefixedVariantId,
        item_price: unitPrice,
        quantity: item.quantity,
        currency: localeInfo.currency
      };
    });
    
    // Calculate subtotal as sum of rounded line prices (unit price * quantity for each item)
    // This ensures the total matches the sum of individual rounded prices
    const subtotal = contents.reduce((sum, item) => {
      return sum + (item.item_price * item.quantity);
    }, 0);
    
    // Build user_data for Meta Pixel (include contact details if available)
    const userData = {
      client_user_agent: navigator.userAgent,
      client_ip_address: null
    };

    // Build payload (helper will add event_name, event_time, event_id, fbp, fbc, user_data)
    const payload = {
      // Custom Data
      custom_data: {
        value: subtotal,
        currency: localeInfo.currency,
        content_type: 'product_group',
        content_ids: contentIds,
        num_items: cart.items.length,
        market: localeInfo.market,
        checkout_type: gateway === 'stripe' ? 'standard' : 'tally',
        contents: contents,
        gateway: gateway,
        catalog_prefix: catalogPrefix,
        locale: localeInfo.locale || localeInfo.market.toLowerCase()
      },
      
      // Cart Data
      cart_data: {
        total_price: cart.total_price,
        total_price_converted: subtotal,
        item_count: cart.items.length,
        items: cart.items.map((item, index) => ({
          product_id: item.product_id,
          variant_id: item.variant_id,
          quantity: item.quantity,
          final_price: item.final_price,
          final_price_converted: roundUpPrice(convertToMarketCurrency(item.final_price)),
          title: item.title || item.product_title || ''
        }))
      },
      user_data: userData,
      // Add plain contact details for n8n processing
      contact_details: {
        email: contactDetails.email || null,
        phone: contactDetails.phone || null
      },
      // Add checkout redirect URL if available
      checkout_url: checkoutUrl || null
    };
    
    return payload;
  }


  // Function to build Tally form URL with cart data
  async function buildTallyURL() {
    const cart = await getCartData();
    
    if (!cart || !cart.items || cart.items.length === 0) {
      return null;
    }

    const url = new URL(TALLY_FORM_URL);
    const items = cart.items;
    const maxItems = Math.min(items.length, 10);
    const hiddenFields = {};

    // Add product data for each item (up to 10)
    for (let i = 0; i < maxItems; i++) {
      const item = items[i];
      const index = i + 1;
      
      const productGid = formatProductGID(item.product_id);
      url.searchParams.set(`product_gid_${index}`, productGid);
      hiddenFields[`product_gid_${index}`] = productGid;
      
      const variantId = item.variant_id.toString();
      url.searchParams.set(`variant_id_${index}`, variantId);
      hiddenFields[`variant_id_${index}`] = variantId;
      
      const quantity = item.quantity.toString();
      url.searchParams.set(`quantity_${index}`, quantity);
      hiddenFields[`quantity_${index}`] = quantity;
      
      const linePriceAED = convertToAED(item.line_price);
      url.searchParams.set(`price_aed_${index}`, linePriceAED);
      hiddenFields[`price_aed_${index}`] = linePriceAED;
    }

    // Add subtotal in AED
    const subtotalAED = convertToAED(cart.total_price);
    url.searchParams.set('subtotal_aed', subtotalAED);
    hiddenFields['subtotal_aed'] = subtotalAED;

    // Add total items count
    const totalItems = items.length.toString();
    url.searchParams.set('total_items', totalItems);
    hiddenFields['total_items'] = totalItems;

    // Add Meta Pixel identifiers
    const metaPixelIds = getMetaPixelIdentifiers();
    url.searchParams.set('fbp', metaPixelIds.fbp || 'null');
    hiddenFields['fbp'] = metaPixelIds.fbp || null;
    
    url.searchParams.set('fbc', metaPixelIds.fbc || 'null');
    hiddenFields['fbc'] = metaPixelIds.fbc || null;

    return {
      url: url.toString(),
      hiddenFields: hiddenFields,
      cart: cart
    };
  }

  // Function to show loading state on button
  function showButtonLoading(button) {
    button.disabled = true;
    button.dataset.processing = 'true';
    button.classList.add('is-loading');
    
    const buttonStyle = window.getComputedStyle(button);
    if (buttonStyle.position === 'static') {
      button.style.position = 'relative';
    }
    
    let textWrapper = button.querySelector('.btn__text');
    if (!textWrapper) {
      const buttonContent = button.innerHTML;
      button.innerHTML = '';
      textWrapper = document.createElement('span');
      textWrapper.className = 'btn__text';
      textWrapper.innerHTML = buttonContent;
      button.appendChild(textWrapper);
    }
    
    if (!button.dataset.originalText) {
      button.dataset.originalText = textWrapper.textContent.trim();
    }
    
    let loader = button.querySelector('.btn__loader');
    if (!loader) {
      loader = document.createElement('span');
      loader.className = 'btn__loader';
      loader.innerHTML = '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style="display: block;"><circle cx="10" cy="10" r="8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-dasharray="12.566 12.566" opacity="0.3"/><circle cx="10" cy="10" r="8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-dasharray="12.566" stroke-dashoffset="12.566"><animate attributeName="stroke-dashoffset" dur="1s" values="12.566;0;12.566" repeatCount="indefinite"/></circle></svg>';
      loader.style.cssText = 'position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); opacity: 1; display: block;';
      button.appendChild(loader);
    } else {
      loader.style.opacity = '1';
      loader.style.display = 'block';
    }
  }

  // Function to hide loading state on button
  function hideButtonLoading(button) {
    button.disabled = false;
    button.dataset.processing = 'false';
    button.classList.remove('is-loading');
    
    const textWrapper = button.querySelector('.btn__text');
    const loader = button.querySelector('.btn__loader');
    
    if (textWrapper) {
      textWrapper.style.opacity = '1';
    }
    
    if (loader) {
      loader.style.opacity = '0';
      loader.style.display = 'none';
    }
  }

  // ============================================
  // Large-order guard
  // ============================================
  // The checkout pipeline supports up to 40 DISTINCT cart lines (worker v2
  // chunked metadata); 25 is the UX-chosen theme limit with headroom under
  // that hard cap. Quantities of the same product do not count. Above the
  // limit we hand the order to the team instead of failing at payment.
  const LARGE_ORDER_MAX_LINES = 25;
  // Carts up to this many lines travel to /pages/checkout-redirect as URL
  // params (the original, proven handoff). Bigger carts would produce URLs
  // that can hit CDN/browser limits, so they hand off via sessionStorage.
  const URL_HANDOFF_MAX_LINES = 10;
  const LARGE_ORDER_SUPPORT_EMAIL = 'hello@duavaa.com';

  const LARGE_ORDER_STRINGS = {
    en: {
      title: 'This order is a little too big for online checkout',
      body: 'Online checkout supports up to {max} different items in one order. Split it into two orders, or share your list with our team and we will arrange everything personally.',
      whatsapp: 'Order on WhatsApp',
      email: 'Email your order list',
      back: 'Back to cart',
      waGreeting: 'Hi Duavaa, I would like to place a large order:',
      emailSubject: 'Large order request'
    },
    ar: {
      title: 'طلبك أكبر قليلاً من حد الدفع الإلكتروني',
      body: 'يدعم الدفع الإلكتروني حتى {max} منتجاً مختلفاً في الطلب الواحد. يمكنك تقسيم طلبك إلى طلبين، أو مشاركة قائمتك مع فريقنا وسنرتب لك كل شيء شخصياً.',
      whatsapp: 'اطلب عبر واتساب',
      email: 'أرسل طلبك بالبريد الإلكتروني',
      back: 'العودة إلى السلة',
      waGreeting: 'مرحباً دوافا، أرغب في تقديم طلب كبير:',
      emailSubject: 'طلب كبير'
    }
  };

  function largeOrderLocale() {
    const lang = (document.documentElement.lang || 'en').toLowerCase();
    return lang.indexOf('ar') === 0 ? 'ar' : 'en';
  }

  // Market prefix of the current page, e.g. '/en-ae' (empty for India paths).
  // The /pages/whatsapp redirect routes to the right number from this prefix.
  function largeOrderMarketRoot() {
    const match = window.location.pathname.match(/^\/((?:en|ar)-[a-z]{2})(?=\/|$)/);
    return match ? '/' + match[1] : '';
  }

  // "1x Product Name" per line, so the team can quote immediately.
  function largeOrderCartSummary(cart) {
    const lines = [];
    for (let i = 0; i < cart.items.length; i++) {
      const item = cart.items[i];
      const name = item.product_title || item.title || 'Item';
      const variant = item.variant_title ? ' (' + item.variant_title + ')' : '';
      lines.push(item.quantity + 'x ' + name + variant);
    }
    let summary = lines.join('\n');
    if (summary.length > 1200) {
      summary = summary.slice(0, 1200) + '\n...';
    }
    return summary;
  }

  function showLargeOrderModal(cart) {
    const locale = largeOrderLocale();
    const t = LARGE_ORDER_STRINGS[locale];
    const existing = document.getElementById('duavaa-large-order-modal');
    if (existing) existing.remove();

    if (!document.getElementById('duavaa-lom-styles')) {
      const style = document.createElement('style');
      style.id = 'duavaa-lom-styles';
      style.textContent = [
        '#duavaa-large-order-modal{position:fixed;inset:0;z-index:99999;background:rgba(0,0,0,0.5);display:flex;align-items:center;justify-content:center;padding:20px;}',
        '.duavaa-lom__card{background:#fff;border-radius:16px;max-width:340px;width:100%;padding:28px 22px 18px;text-align:center;font-family:inherit;box-shadow:0 10px 40px rgba(0,0,0,0.2);}',
        '.duavaa-lom__icon{width:46px;height:46px;border-radius:50%;background:#faeeda;display:flex;align-items:center;justify-content:center;margin:0 auto 14px;font-size:22px;}',
        '.duavaa-lom__title{font-size:17px;font-weight:600;color:#1a1a1a;margin:0 0 8px;line-height:1.35;}',
        '.duavaa-lom__body{font-size:13.5px;color:#555;line-height:1.55;margin:0 0 18px;}',
        '.duavaa-lom__btn{display:flex;align-items:center;justify-content:center;gap:8px;width:100%;box-sizing:border-box;border-radius:8px;padding:12px;font-size:14px;font-weight:500;text-decoration:none;cursor:pointer;margin-bottom:8px;font-family:inherit;}',
        '.duavaa-lom__btn--wa{background:#1da851;color:#fff;border:none;}',
        '.duavaa-lom__btn--email{background:transparent;color:#1a1a1a;border:1px solid #d5d5d5;}',
        '.duavaa-lom__btn--back{background:transparent;color:#777;border:none;font-size:13px;padding:9px;margin-bottom:0;}',
        '@media (max-width:480px){#duavaa-large-order-modal{align-items:flex-end;padding:0;}.duavaa-lom__card{max-width:none;border-radius:16px 16px 0 0;padding-bottom:calc(18px + env(safe-area-inset-bottom));}}'
      ].join('');
      document.head.appendChild(style);
    }

    const summary = largeOrderCartSummary(cart);
    const waText = t.waGreeting + '\n' + summary;
    const waHref = largeOrderMarketRoot() + '/pages/whatsapp?text=' + encodeURIComponent(waText);
    const emailHref = 'mailto:' + LARGE_ORDER_SUPPORT_EMAIL +
      '?subject=' + encodeURIComponent(t.emailSubject) +
      '&body=' + encodeURIComponent(waText);

    const overlay = document.createElement('div');
    overlay.id = 'duavaa-large-order-modal';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');

    const card = document.createElement('div');
    card.className = 'duavaa-lom__card';
    if (locale === 'ar') card.setAttribute('dir', 'rtl');

    const icon = document.createElement('div');
    icon.className = 'duavaa-lom__icon';
    icon.innerHTML = '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ba7517" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>';

    const title = document.createElement('p');
    title.className = 'duavaa-lom__title';
    title.textContent = t.title;

    const body = document.createElement('p');
    body.className = 'duavaa-lom__body';
    body.textContent = t.body.replace('{max}', String(LARGE_ORDER_MAX_LINES));

    const waBtn = document.createElement('a');
    waBtn.className = 'duavaa-lom__btn duavaa-lom__btn--wa';
    waBtn.href = waHref;
    waBtn.target = '_blank';
    waBtn.rel = 'noopener';
    waBtn.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/></svg>' + '<span></span>';
    waBtn.querySelector('span').textContent = t.whatsapp;

    const emailBtn = document.createElement('a');
    emailBtn.className = 'duavaa-lom__btn duavaa-lom__btn--email';
    emailBtn.href = emailHref;
    emailBtn.textContent = t.email;

    const backBtn = document.createElement('button');
    backBtn.type = 'button';
    backBtn.className = 'duavaa-lom__btn duavaa-lom__btn--back';
    backBtn.textContent = t.back;

    function closeModal() {
      overlay.remove();
      document.removeEventListener('keydown', onKeydown);
    }
    function onKeydown(ev) {
      if (ev.key === 'Escape') closeModal();
    }
    backBtn.addEventListener('click', closeModal);
    overlay.addEventListener('click', function (ev) {
      if (ev.target === overlay) closeModal();
    });
    document.addEventListener('keydown', onKeydown);

    card.appendChild(icon);
    card.appendChild(title);
    card.appendChild(body);
    card.appendChild(waBtn);
    card.appendChild(emailBtn);
    card.appendChild(backBtn);
    overlay.appendChild(card);
    document.body.appendChild(overlay);
  }

  // Shared with buy-now-international.js (Buy Now checks out the whole cart too).
  window.DuavaaLargeOrderModal = {
    maxLines: LARGE_ORDER_MAX_LINES,
    show: showLargeOrderModal
  };

  // Function to handle checkout button click
  async function handleTallyCheckout(e) {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    }

    const button = e.currentTarget || e.target || (e.target && e.target.closest('[data-tally-checkout-button]'));
    
    if (!button) {
      console.warn('[Tally Checkout] No button found in handleTallyCheckout');
      return;
    }
    
    // Disable button to prevent multiple clicks
    if (button.disabled || button.dataset.processing === 'true') {
      return;
    }
    
    button.dataset.processing = 'true';
    showButtonLoading(button);
    
    console.log('[Tally Checkout] Button clicked, starting checkout process...');

    try {
      // Fetch cart data with timeout
      const cartPromise = getCartData();
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Cart fetch timeout')), 3000)
      );
      
      const cart = await Promise.race([cartPromise, timeoutPromise]);
      
      if (!cart || !cart.items || cart.items.length === 0) {
        hideButtonLoading(button);
        alert(themeStr('cartEmptyAlert', 'Your cart is empty. Please add items to proceed.'));
        return;
      }

      // Too many distinct lines for the Stripe worker: hand off to the team
      // instead of failing at payment (or truncating to the 10-line payload cap).
      if (cart.items.length > LARGE_ORDER_MAX_LINES) {
        hideButtonLoading(button);
        showLargeOrderModal(cart);
        return;
      }

      // Detect locale/market using MarketConfig
      let marketInfo = null;
      let localeInfo = { currency: 'INR', market: 'INR' };
      
      if (window.MarketConfig && typeof window.MarketConfig.getCurrentMarket === 'function') {
        marketInfo = window.MarketConfig.getCurrentMarket();
        if (marketInfo && marketInfo.config) {
          localeInfo = {
            currency: marketInfo.config.currency,
            market: marketInfo.config.market
          };
        }
      } else {
        // Fallback to old detection method
      const isEnAe = (window.shopifyLocale && window.shopifyLocale.isEnAe) ||
                     window.location.pathname.includes('/en-ae/') ||
                     window.location.pathname.includes('/ar-ae/') ||
                     window.location.pathname.includes('/en-sa/') ||
                     window.location.pathname.includes('/ar-sa/') ||
                     window.location.hostname.includes('en-ae') ||
                     window.location.hostname.includes('ar-ae') ||
                     window.location.hostname.includes('en-sa') ||
                     window.location.hostname.includes('ar-sa') ||
                     window.location.href.includes('/en-ae/') ||
                     window.location.href.includes('/ar-ae/') ||
                     window.location.href.includes('/en-sa/') ||
                     window.location.href.includes('/ar-sa/');
      
        localeInfo = isEnAe 
        ? { currency: 'AED', market: 'UAE' } 
        : { currency: 'INR', market: 'INR' };
      }

      // Build checkout-redirect URL with cart data (bypassing custom checkout form)
      // Stripe Checkout will collect all customer information directly
      let baseURL = '/pages/checkout-redirect';
      if (marketInfo && marketInfo.key !== 'default' && marketInfo.config.path) {
        // Normalize path: remove trailing slash, ensure it starts with /
        let marketPath = marketInfo.config.path.trim();
        if (marketPath && !marketPath.startsWith('/')) {
          marketPath = '/' + marketPath;
        }
        marketPath = marketPath.replace(/\/+$/, ''); // Remove trailing slashes
        baseURL = marketPath + '/pages/checkout-redirect';
      } else {
        // Fallback: detect from URL path if MarketConfig is not available
        const pathMatch = window.location.pathname.match(/^\/(en-[a-z]{2})(?:\/|$)/);
        if (pathMatch) {
          baseURL = pathMatch[1] + '/pages/checkout-redirect';
        }
      }
      
      const params = new URLSearchParams();
      
      params.set('total_items', cart.items.length.toString());
      
      // Add product data and calculate subtotal as sum of rounded line prices.
      // The guard above already capped the cart at LARGE_ORDER_MAX_LINES, so this
      // Math.min never truncates a real cart (it replaces the old silent 10-line cap).
      const maxItems = Math.min(cart.items.length, LARGE_ORDER_MAX_LINES);
      let calculatedSubtotal = 0;

      // ---- Offer Engine (international): discounted unit prices for the current country ----
      // Mirrors the cart display (offer-cart-display.js) and the Buy Now path so the charge,
      // the visible cart total, and the resulting Shopify order all reconcile.
      const offerBaseItems = [];
      for (let oi = 0; oi < maxItems; oi++) {
        const oit = cart.items[oi];
        const oprops = (window.CartLineItemProperties && typeof window.CartLineItemProperties.toPlainObject === 'function')
          ? window.CartLineItemProperties.toPlainObject(oit)
          : (oit.properties || {});
        offerBaseItems.push({
          key: String(oit.variant_id) + ':' + oi,
          quantity: oit.quantity,
          unitPrice: roundUpPrice(convertToMarketCurrency(oit.final_price)),
          isMto: !!(oprops && oprops._Service) // only the plating service FEE line; the plated product itself stays offer-eligible
        });
      }
      const offerResult = (window.OfferEngine && typeof window.OfferEngine.apply === 'function')
        ? window.OfferEngine.apply(offerBaseItems)
        : { applied: false, totalDiscount: 0, offerLabel: '', items: offerBaseItems.map(function (x) { return { discountedUnitPrice: x.unitPrice }; }) };

      for (let i = 0; i < maxItems; i++) {
        const item = cart.items[i];
        const index = i + 1;
        
        params.set(`product_gid_${index}`, formatProductGID(item.product_id));
        params.set(`variant_id_${index}`, item.variant_id.toString());
        params.set(`quantity_${index}`, item.quantity.toString());
        
        // IMPORTANT: Stripe API expects unit price (final_price), not line price (line_price)
        // The API will multiply unit price by quantity automatically
        // Use final_price (unit price) - Stripe multiplies by quantity.
        // Offer Engine may have discounted this line for the current country.
        const baseUnitPrice = roundUpPrice(convertToMarketCurrency(item.final_price));
        const unitPrice = (offerResult.items[i] && offerResult.items[i].discountedUnitPrice != null)
          ? offerResult.items[i].discountedUnitPrice
          : baseUnitPrice;
        params.set(`price_${localeInfo.currency.toLowerCase()}_${index}`, unitPrice.toFixed(2));
        // Keep price_aed for backward compatibility (also use unit price)
        params.set(`price_aed_${index}`, unitPrice.toFixed(2));
        
        // Calculate line price (unit price * quantity) and add to subtotal
        const linePrice = unitPrice * item.quantity;
        calculatedSubtotal += linePrice;
        
        // Also add line price for reference
        params.set(`line_price_${localeInfo.currency.toLowerCase()}_${index}`, linePrice.toFixed(2));
        
        // Add product name for Stripe checkout display
        const productName = item.title || item.product_title || '';
        const variantTitle = item.variant_title || '';
        const displayName = variantTitle ? `${productName} - ${variantTitle}` : productName;
        if (displayName) {
          params.set(`product_name_${index}`, displayName);
        }
        if (window.CartLineItemProperties && typeof window.CartLineItemProperties.jsonForUrlParam === 'function') {
          const propsJson = window.CartLineItemProperties.jsonForUrlParam(item);
          if (propsJson) {
            params.set(`line_item_properties_${index}`, propsJson);
          }
        }
      }
      
      // Set subtotal as sum of rounded line prices (ensures total matches sum of individual prices)
      params.set('subtotal', calculatedSubtotal.toFixed(2));

      // Offer Engine (approach A): line prices above are already discounted, so the total
      // reconciles automatically. These are for order tagging / reporting only.
      if (offerResult.applied) {
        params.set('discount_code', offerResult.offerLabel || '');
        params.set('discount_amount', offerResult.totalDiscount.toFixed(2));
      }
      
      // Send InitiateCheckout webhook early (when Checkout button is clicked)
      // This tracks the initial checkout intent before contact details collection
      console.log('[Tally Checkout] Sending early InitiateCheckout webhook (button clicked)...');
      sendInitiateCheckoutWebhook(cart, localeInfo, {}, null);
      
      // Collect contact details before checkout
      // IMPORTANT: This will only resolve when user clicks "Continue to Payment" button
      // If user closes the dialog without submitting, it will reject and we return early
      console.log('[Tally Checkout] Collecting contact details...');
      let contactDetails = { email: '', phone: '' };
      
      try {
        contactDetails = await collectContactDetails();
        console.log('[Tally Checkout] ✅ Contact details collected:', { email: contactDetails.email ? '***' : '', phone: contactDetails.phone ? '***' : '' });
      } catch (error) {
        // User cancelled contact details collection (closed dialog without submitting)
        console.log('[Tally Checkout] User cancelled contact details collection');
        hideButtonLoading(button);
        return; // Do NOT redirect - user cancelled
      }

      // Add email to URL params (for checkout-redirect page to pass to Stripe)
      if (contactDetails.email) {
        params.set('email', contactDetails.email);
      }

      // Propagate an explicit test_mode flag from the cart page URL so the
      // whole chain (checkout-redirect -> test worker -> Stripe test key ->
      // test webhook -> n8n test workflow) can be exercised on the live site
      // by simply visiting the cart with ?test_mode=true.
      const pageTestMode = new URLSearchParams(window.location.search).get('test_mode');
      if (pageTestMode === 'true' || pageTestMode === '1' || pageTestMode === 'false' || pageTestMode === '0') {
        params.set('test_mode', pageTestMode);
      }

      // Big carts hand the payload to checkout-redirect via sessionStorage: a
      // 10+ line cart as URL params can exceed CDN/browser URL limits. Small
      // carts keep the original URL handoff (unchanged, battle-tested path).
      // The redirect happens in the same tab, so sessionStorage survives it.
      let handoffQuery = params.toString();
      if (cart.items.length > URL_HANDOFF_MAX_LINES) {
        try {
          const payloadKey = 'co_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
          sessionStorage.setItem('duavaa_checkout_payload_' + payloadKey, handoffQuery);
          const shortParams = new URLSearchParams();
          shortParams.set('payload_key', payloadKey);
          shortParams.set('total_items', cart.items.length.toString());
          if (pageTestMode != null && params.has('test_mode')) {
            // test_mode must stay in the VISIBLE url: checkout-redirect reads it
            // from location.search to pick the worker before touching the payload.
            shortParams.set('test_mode', params.get('test_mode'));
          }
          handoffQuery = shortParams.toString();
        } catch (storageError) {
          // sessionStorage unavailable (rare private-mode edge): fall back to the
          // long URL rather than blocking checkout.
          console.warn('[Tally Checkout] sessionStorage handoff unavailable, using URL params', storageError);
        }
      }

      const checkoutURL = `${baseURL}?${handoffQuery}`;
      
      // If in preview mode, preserve the preview parameters
      let finalURL = checkoutURL;
      if (window.location.href.includes('/preview/')) {
        const previewMatch = window.location.href.match(/\/preview\/[^\/]+/);
        if (previewMatch) {
          finalURL = previewMatch[0] + checkoutURL;
        }
      }
      
      // Send InitiateCheckout webhook again with complete data (contact details + checkout URL)
      // This tracks the final checkout initiation when user clicks "Proceed to Payment"
      // (truly fire and forget - don't await or catch)
      console.log('[Tally Checkout] Sending InitiateCheckout webhook with complete data (Proceed to Payment clicked)...');
      sendInitiateCheckoutWebhook(cart, localeInfo, contactDetails, finalURL);
      
      // Add a small delay before redirect to ensure webhook is sent
      // But don't wait too long - 100ms should be enough
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Close the contact details dialog just before redirect
      // This keeps the loading state visible until redirect happens
      if (window.ContactDetailsCollector && typeof window.ContactDetailsCollector.closeDialog === 'function') {
        try {
          window.ContactDetailsCollector.closeDialog();
        } catch (closeError) {
          console.warn('[Tally Checkout] Error closing contact details dialog:', closeError);
        }
      }
      
      // Small delay to allow dialog close animation (optional, but improves UX)
      await new Promise(resolve => setTimeout(resolve, 50));
      
      // Redirect to checkout-redirect ONLY after user clicked "Continue to Payment"
      // (contactDetails was collected successfully)
      try {
        window.location.href = finalURL;
      } catch (redirectError) {
        window.location.replace(finalURL);
      }
      
      // Emergency fallback (only if redirect didn't work)
      const currentPath = window.location.pathname;
      setTimeout(() => {
        if (window.location.pathname === currentPath && !window.location.pathname.includes('checkout-redirect')) {
          window.location.replace(finalURL);
        }
      }, 200);
      
      return;
    } catch (error) {
      // Error occurred during checkout process (not user cancellation)
      console.error('[Tally Checkout] Error during checkout:', error);
      hideButtonLoading(button);
      
      // Show error to user - do NOT redirect automatically
      alert(themeStr('checkoutErrorRetry', 'An error occurred during checkout. Please try again.'));
      return;
    }
  }

  // Track initialized buttons to prevent duplicate listeners
  const initializedButtons = new WeakSet();
  
  // Track if delegation is already set up to prevent duplicates
  let delegationSetup = false;
  
  // Use event delegation to survive button cloning
  function setupEventDelegation() {
    // Prevent duplicate event listeners
    if (delegationSetup) {
      return;
    }
    
    // Ensure document.body exists before setting up delegation
    if (!document.body) {
      // Wait for body to be available
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', setupEventDelegation);
      } else {
        // Fallback: try again after a short delay
        setTimeout(setupEventDelegation, 50);
      }
      return;
    }
    
    // Set up on document.body to catch all clicks (capture phase for early interception)
    document.body.addEventListener('click', function(e) {
      // First check for explicit data-tally-checkout-button attribute
      let button = e.target.closest('[data-tally-checkout-button]');
      
      // If not found, check if it's a checkout button in an international market
      if (!button) {
        const target = e.target;
        if (target && (target.tagName === 'BUTTON' || target.tagName === 'A')) {
          // Check if international market using multiple methods
          let isInternational = false;
          
          // Method 1: Use MarketConfig if available
          if (window.MarketConfig && 
              typeof window.MarketConfig.isInternationalMarket === 'function') {
            isInternational = window.MarketConfig.isInternationalMarket();
          }
          
          // Method 2: Use WebhookHelper if available
          if (!isInternational && window.WebhookHelper && 
              typeof window.WebhookHelper.isInternationalMarket === 'function') {
            isInternational = window.WebhookHelper.isInternationalMarket();
          }
          
          // Method 3: Fallback path-based detection (handle both with and without trailing slash)
          if (!isInternational) {
            const path = window.location.pathname.toLowerCase();
            isInternational = /^\/[a-z]{2}-(?:ae|sa|gb|sg|us|eu|ca|au)(?:\/|$)/.test(path) ||
                              /\/[a-z]{2}-(?:ae|sa|gb|sg|us|eu|ca|au)(?:\/|$)/.test(path);
          }
          
          // Method 4: Check shopifyLocale
          if (!isInternational && window.shopifyLocale && window.shopifyLocale.code) {
            // Any language on a known market counts, so match the market suffix.
            const INTERNATIONAL_MARKET_LOCALE = /^[a-z]{2}-(?:ae|sa|gb|sg|us|eu|ca|au)$/;
            isInternational = INTERNATIONAL_MARKET_LOCALE.test(window.shopifyLocale.code);
          }
          
          if (isInternational) {
            const isCheckoutButton = target.classList.contains('cart__checkout') ||
                                     target.classList.contains('cart__checkout--tally') ||
                                     target.name === 'checkout' ||
                                     (target.textContent && target.textContent.toLowerCase().trim().includes('checkout'));
            
            const inCartArea = target.closest('[data-cart-page], .cart, .cart-drawer, [data-cart-drawer], [data-checkout-buttons], .drawer__foot');
            
            if (isCheckoutButton && inCartArea) {
              // Add the attribute so it gets picked up by initTallyCheckout
              target.setAttribute('data-tally-checkout-button', 'true');
              button = target;
            }
          }
        }
      }
      
      if (button) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        // Also prevent any form submission
        const form = button.closest('form');
        if (form && form.hasAttribute('data-disable-form-submit')) {
          form.setAttribute('data-original-action', form.action);
          form.action = 'javascript:void(0)';
        }
        
        handleTallyCheckout({
          preventDefault: () => {},
          stopPropagation: () => {},
          stopImmediatePropagation: () => {},
          currentTarget: button,
          target: button
        });
        
        return false;
      }
    }, true); // Use capture phase for early interception
    
    // Also intercept form submissions for buttons with data-tally-checkout-button
    document.body.addEventListener('submit', function(e) {
      const form = e.target;
      if (!form || form.tagName !== 'FORM') return;
      
      // Check if submitter is a button with data-tally-checkout-button
      const submitter = e.submitter;
      if (submitter && submitter.hasAttribute && submitter.hasAttribute('data-tally-checkout-button')) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        handleTallyCheckout({
          preventDefault: () => {},
          stopPropagation: () => {},
          stopImmediatePropagation: () => {},
          currentTarget: submitter,
          target: submitter
        });
        
        return false;
      }
      
      // Also check if form contains a button with data-tally-checkout-button
      const tallyButton = form.querySelector('[data-tally-checkout-button]');
      if (tallyButton && submitter && (submitter === tallyButton || tallyButton.contains(submitter))) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        handleTallyCheckout({
          preventDefault: () => {},
          stopPropagation: () => {},
          stopImmediatePropagation: () => {},
          currentTarget: tallyButton,
          target: tallyButton
        });
        
        return false;
      }
    }, true); // Use capture phase for early interception
    
    delegationSetup = true;
  }

  // Initialize on DOM ready
  function initTallyCheckout() {
    const tallyButtons = document.querySelectorAll('[data-tally-checkout-button]');
    
    if (tallyButtons.length === 0) {
      // Try to find buttons by class or text (for international markets)
        // Check if international market using multiple methods
        let isInternational = false;
        
        // Method 1: Use MarketConfig if available
        if (window.MarketConfig && 
            typeof window.MarketConfig.isInternationalMarket === 'function') {
          isInternational = window.MarketConfig.isInternationalMarket();
        }
        
        // Method 2: Use WebhookHelper if available
        if (!isInternational && window.WebhookHelper && 
            typeof window.WebhookHelper.isInternationalMarket === 'function') {
          isInternational = window.WebhookHelper.isInternationalMarket();
        }
        
        // Method 3: Fallback path-based detection (handle both with and without trailing slash)
        if (!isInternational) {
          const path = window.location.pathname.toLowerCase();
          isInternational = /^\/[a-z]{2}-(?:ae|sa|gb|sg|us|eu|ca|au)(?:\/|$)/.test(path) ||
                            /\/[a-z]{2}-(?:ae|sa|gb|sg|us|eu|ca|au)(?:\/|$)/.test(path);
        }
        
        // Method 4: Check shopifyLocale
        if (!isInternational && window.shopifyLocale && window.shopifyLocale.code) {
          // Any language on a known market counts, so match the market suffix.
          const INTERNATIONAL_MARKET_LOCALE = /^[a-z]{2}-(?:ae|sa|gb|sg|us|eu|ca|au)$/;
          isInternational = INTERNATIONAL_MARKET_LOCALE.test(window.shopifyLocale.code);
        }
      
      if (isInternational) {
        const classSelectors = [
          '.cart__checkout--tally',
          '.cart__checkout',
          'button[type="button"].cart__checkout',
          'button.btn--primary.cart__checkout'
        ];
        
        let foundButton = null;
        for (const selector of classSelectors) {
          const buttons = document.querySelectorAll(selector);
          if (buttons.length > 0) {
            foundButton = buttons[0];
            break;
          }
        }
        
        // If still not found, search by text content (check both cart drawer and cart page)
        if (!foundButton) {
          const allButtons = document.querySelectorAll('button, a.btn, [role="button"]');
          const checkoutButtons = Array.from(allButtons).filter(btn => {
            const text = btn.textContent.toLowerCase().trim();
            const inCartArea = btn.closest('.cart-drawer, .drawer, [data-cart-drawer], .cart__buttons, .drawer__foot, [data-cart-page], .cart__aside, [data-checkout-buttons]');
            const isCheckoutButton = text.includes('checkout') || 
                                     text.includes('complete order') || 
                                     btn.classList.contains('cart__checkout') ||
                                     btn.classList.contains('cart__checkout--tally');
            return isCheckoutButton && inCartArea;
          });
          
          if (checkoutButtons.length > 0) {
            foundButton = checkoutButtons[0];
          } else {
            // Try cart drawer first
            const drawer = document.querySelector('.cart-drawer, .drawer--cart, [data-cart-drawer]');
            if (drawer) {
              const drawerButtons = drawer.querySelectorAll('button, a.btn');
              if (drawerButtons.length > 0) {
                foundButton = drawerButtons[drawerButtons.length - 1];
              }
            }
            // If not found in drawer, try cart page
            if (!foundButton) {
              const cartPage = document.querySelector('[data-cart-page], .cart');
              if (cartPage) {
                const cartPageButtons = cartPage.querySelectorAll('button.cart__checkout, button.cart__checkout--tally, button[name="checkout"]');
                if (cartPageButtons.length > 0) {
                  foundButton = cartPageButtons[0];
                }
              }
            }
          }
        }
        
        // If we found a button, add the attribute and attach handler
        if (foundButton && !foundButton.dataset.tallyHandlerAttached) {
          foundButton.setAttribute('data-tally-checkout-button', 'true');
          
          initializedButtons.add(foundButton);
          foundButton.dataset.tallyHandlerAttached = 'true';
          if (foundButton.type !== 'button') {
            foundButton.type = 'button';
          }
          foundButton.addEventListener('click', handleTallyCheckout, true);
          
          // Handle form submission
          const form = foundButton.closest('form');
          if (form) {
            // Remove form action to prevent native submission
            const originalAction = form.action;
            form.setAttribute('data-original-action', originalAction);
            form.action = 'javascript:void(0)';
            
            // Add submit handler with capture phase
            form.addEventListener('submit', function(e) {
              if (e.submitter === foundButton || (e.submitter && foundButton.contains(e.submitter))) {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                const clickEvent = new MouseEvent('click', {
                  bubbles: true,
                  cancelable: true,
                  view: window
                });
                foundButton.dispatchEvent(clickEvent);
                return false;
              }
            }, true);
          }
          
          return;
        }
      }
      
      return;
    }
    
    tallyButtons.forEach(function(button, index) {
      // Skip if already initialized
      if (initializedButtons.has(button)) {
        return;
      }
      
      // Mark as initialized
      initializedButtons.add(button);
      button.dataset.tallyHandlerAttached = 'true';
      
      // Ensure button type is 'button' to prevent form submission
      if (button.type !== 'button') {
        button.type = 'button';
      }
      
      // Add click handler
      button.addEventListener('click', handleTallyCheckout, true);
      
      // Also add a direct onclick handler as backup
      button.onclick = function(e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        handleTallyCheckout(e);
        return false;
      };
      
      // Also prevent any form submission if button is inside a form
      const form = button.closest('form');
      if (form) {
        // Remove form action to prevent native submission
        const originalAction = form.action;
        form.setAttribute('data-original-action', originalAction);
        form.action = 'javascript:void(0)';
        
        // Add submit handler with capture phase
        form.addEventListener('submit', function(e) {
          if (e.submitter === button || (e.submitter && button.contains(e.submitter))) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            const clickEvent = new MouseEvent('click', {
              bubbles: true,
              cancelable: true,
              view: window
            });
            button.dispatchEvent(clickEvent);
            return false;
          }
        }, true);
      }
    });
  }

  // Expose initTallyCheckout globally so preventMagicCheckout script can call it
  window.initTallyCheckout = initTallyCheckout;

  // Debounce function to prevent excessive initialization calls
  let initTimeout = null;
  function debouncedInit() {
    if (initTimeout) {
      clearTimeout(initTimeout);
    }
    initTimeout = setTimeout(function() {
      initTallyCheckout();
      initTimeout = null;
    }, 100);
  }

  // Set up event delegation when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      setupEventDelegation();
      // Initialize immediately on DOM ready
      initTallyCheckout();
      // Also initialize after a short delay to catch late-loading buttons
      setTimeout(initTallyCheckout, 200);
    });
  } else {
    // DOM already ready, set up delegation and initialize immediately
    setupEventDelegation();
    initTallyCheckout();
    setTimeout(initTallyCheckout, 200);
  }

  // Watch for cart drawer opening/closing (optimized observer)
  let drawerWatchAttempts = 0;
  function watchCartDrawer() {
    const cartDrawer = document.querySelector('.cart-drawer, .drawer--cart, [data-cart-drawer]');
    if (cartDrawer) {
      // Disable form submission for international markets
      const form = cartDrawer.querySelector('form[data-cart-form]');
      if (form && form.hasAttribute('data-disable-form-submit')) {
        form.setAttribute('data-original-action', form.action);
        form.action = 'javascript:void(0)';
        
        // Also add submit handler as backup
        form.addEventListener('submit', function(e) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          return false;
        }, true);
      }
      
      const drawerObserver = new MutationObserver(function(mutations) {
        let shouldInit = false;
        
        mutations.forEach(function(mutation) {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            const isOpen = !cartDrawer.classList.contains('is-hidden') && 
                          !cartDrawer.classList.contains('hidden') &&
                          cartDrawer.offsetWidth > 0;
            if (isOpen) {
              shouldInit = true;
            }
          }
          
          // Check for new buttons only if nodes were added
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            const hasNewButtons = Array.from(mutation.addedNodes).some(node => 
              node.nodeType === 1 && (
                (node.hasAttribute && node.hasAttribute('data-tally-checkout-button')) ||
                (node.querySelector && node.querySelector('[data-tally-checkout-button]')) ||
                (node.tagName === 'BUTTON' && node.closest('.cart-drawer, .drawer--cart'))
              )
            );
            if (hasNewButtons) {
              shouldInit = true;
            }
          }
        });
        
        if (shouldInit) {
          // Initialize immediately when drawer opens (don't debounce for drawer open)
          initTallyCheckout();
          
          // Also disable form again in case it was re-enabled
          const form = cartDrawer.querySelector('form[data-cart-form]');
          if (form && form.hasAttribute('data-disable-form-submit')) {
            form.setAttribute('data-original-action', form.action);
            form.action = 'javascript:void(0)';
          }
        }
      });
      
      drawerObserver.observe(cartDrawer, {
        attributes: true,
        attributeFilter: ['class'],
        childList: true,
        subtree: true
      });
    } else {
      drawerWatchAttempts++;
      // Retry up to 10 times (5 seconds total)
      if (drawerWatchAttempts < 10) {
        setTimeout(watchCartDrawer, 500);
      }
    }
  }
  
  watchCartDrawer();

  // Optimized MutationObserver for dynamically loaded content (throttled to reduce CPU usage)
  if (typeof MutationObserver !== 'undefined') {
    let lastInitTime = 0;
    const observer = new MutationObserver(function(mutations) {
      // Throttle initialization to max once per 500ms to reduce CPU usage
      const now = Date.now();
      if (now - lastInitTime < 500) {
        return;
      }
      
      const hasNewButtons = mutations.some(mutation => 
        Array.from(mutation.addedNodes).some(node => 
          node.nodeType === 1 && (
            (node.hasAttribute && node.hasAttribute('data-tally-checkout-button')) ||
            (node.querySelector && node.querySelector('[data-tally-checkout-button]'))
          )
        )
      );
      
      const hasClonedButtons = mutations.some(mutation => 
        Array.from(mutation.addedNodes).some(node => {
          if (node.nodeType === 1 && node.hasAttribute && node.hasAttribute('data-tally-checkout-button')) {
            return !node.dataset.tallyHandlerAttached;
          }
          return false;
        })
      );
      
      if (hasNewButtons || hasClonedButtons) {
        lastInitTime = now;
        debouncedInit();
      }
    });
    
    // Observe document.body with subtree (needed to catch buttons added anywhere)
    // But throttled above to reduce CPU impact
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // Additional delayed initialization to catch late-loading buttons
  setTimeout(function() {
    initTallyCheckout();
  }, 1000);

  // Listen for cart drawer open events
  document.addEventListener('cart:open', function() {
    debouncedInit();
  });
})();
